import { NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    signal,
    Signal,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { FormField, FieldTree } from "@angular/forms/signals";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Item } from "../../pojo/Item";
import { Nullable } from "../../pojo/Util";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25ItemUnwrapperComponent } from "../s25-item/s25.item.unwrapper.component";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { Task } from "../../pojo/Task";
import { NotificationPolicyModel } from "./notification.policy.model";
import { NotificationPolicyMapper } from "./notification.policy.mapper";
import { S25Util } from "../../util/s25-util";
import { TypedTemplateContextDirective } from "../../standalone/typed.template.directive";

const USERS_ONLY_FILTER = "&is_r25user=1";

type EditableContactRowContext = {
    contact: NotificationPolicyModel.ContactDisplay;
    removeFallback: string;
    moveLabel: string;
    moveTarget: NotificationPolicyModel.ContactRole;
};

type ContactSectionConfig = {
    role: NotificationPolicyModel.ContactRole;
    title: string;
    removeFallback: string;
    moveLabel: string;
    moveTarget: NotificationPolicyModel.ContactRole;
    fieldId: string;
    labelId: string;
    contacts: Signal<NotificationPolicyModel.ContactDisplay[]>;
    dropdownSelection: WritableSignal<Nullable<DropDownItem>>;
};

@Component({
    selector: "s25-ng-notification-policy-sections",
    host: { class: "notification-policy-sections" },
    imports: [
        NgTemplateOutlet,
        FormField,
        S25DropdownPaginatedModule,
        S25ItemUnwrapperComponent,
        S25IconButtonComponent,
        TypedTemplateContextDirective,
    ],
    template: `
        @if (readOnly()) {
            @for (section of contactSections; track section.role) {
                @if (section.contacts().length) {
                    <section class="notification-policy-sections__block c-margin-bottom--single">
                        <div class="notification-policy-sections__header">
                            <span class="ngBold">{{ section.title }}</span>
                            <span class="ngFinePrint">{{ section.contacts().length }} contact(s)</span>
                        </div>
                        <div class="notification-policy-sections__body">
                            @if (section.role === "approver" && quorumReadOnlyLabel()) {
                                <p class="ngFinePrint">{{ quorumReadOnlyLabel() }}</p>
                            }
                            @for (contact of section.contacts(); track contact.itemId) {
                                <div class="notification-policy--flex c-margin-bottom--quarter">
                                    <s25-item-unwrapper
                                        [modelBean]="contact"
                                        [itemTypeId]="Item.Ids.Contact"
                                        [includeTypeIcon]="true"
                                    />
                                </div>
                            }
                        </div>
                    </section>
                }
            }
            @if (layout().showApprovalWindow) {
                <section class="notification-policy-sections__block c-margin-bottom--single">
                    <span class="ngBold c-displayBlock">Approval window</span>
                    <span class="c-displayBlock c-margin-top--half"
                        >Approval required within
                        <b>{{ days() }} days, {{ hours() }} hours, and {{ minutes() }} minutes</b>
                        of event creation date</span
                    >
                </section>
            }
        } @else {
            <ng-template
                #editableContactRow
                [templateContext]="editableContactRowContext"
                let-contact="contact"
                let-removeFallback="removeFallback"
                let-moveLabel="moveLabel"
                let-moveTarget="moveTarget"
            >
                <div class="notification-policy--flex c-margin-bottom--quarter">
                    <s25-ng-icon-button
                        class="c-margin-right--half"
                        [type]="'close'"
                        [ariaLabel]="'Remove ' + (contact.itemName || removeFallback)"
                        (clicked)="removeContact(contact)"
                    />
                    <s25-item-unwrapper
                        [modelBean]="contact"
                        [itemTypeId]="Item.Ids.Contact"
                        [includeTypeIcon]="true"
                    />
                    <button
                        type="button"
                        class="aw-button aw-button--outline c-margin-left--half"
                        (click)="moveContactToTarget(contact, moveTarget)"
                    >
                        {{ moveLabel }}
                    </button>
                </div>
            </ng-template>
            @if (editor().pickerError()) {
                <p class="ngRed c-margin-bottom--single" role="alert">{{ editor().pickerError() }}</p>
            }
            @for (section of contactSections; track section.role) {
                <section class="notification-policy-sections__block c-margin-bottom--single">
                    <div class="notification-policy-sections__header">
                        <span class="ngBold">{{ section.title }}</span>
                        <span class="ngFinePrint">{{ section.contacts().length }} contact(s)</span>
                    </div>
                    <div class="notification-policy-sections__body">
                        @for (contact of section.contacts(); track contact.itemId) {
                            <ng-container
                                [ngTemplateOutlet]="editableContactRow"
                                [ngTemplateOutletContext]="{
                                    contact: contact,
                                    removeFallback: section.removeFallback,
                                    moveLabel: section.moveLabel,
                                    moveTarget: section.moveTarget,
                                }"
                            />
                        }
                        @if (section.role === "approver" && layout().showQuorum && section.contacts().length >= 2) {
                            <label class="c-displayBlock c-margin-top--half">
                                <span class="ngBold">Approval Type</span>
                                <select
                                    class="c-selectInput c-displayBlock c-margin-top--quarter"
                                    id="approverQuorum"
                                    [formField]="policyForm().approvalType.itemId"
                                >
                                    @for (option of quorumOptions; track option.itemId) {
                                        <option [value]="option.itemId">{{ option.itemName }}</option>
                                    }
                                </select>
                            </label>
                            @let quorumField = policyForm().approvalType.itemId();
                            @if ((quorumField.touched() || submitAttempted()) && quorumField.invalid()) {
                                <p class="ngRed">{{ quorumField.errors()[0].message }}</p>
                            }
                        }
                        <div class="c-margin-top--half">
                            <label [id]="section.labelId" class="ngBold c-displayBlock c-margin-bottom--quarter"
                                >Add a contact</label
                            >
                            <s25-ng-dropdown-search-criteria
                                [type]="'contacts'"
                                [customFilterValue]="usersOnlyFilter"
                                [value]="section.dropdownSelection()"
                                [renderItem]="false"
                                [noSelectionUsePlaceholder]="true"
                                [placeholder]="'Select a contact'"
                                [fieldId]="section.fieldId"
                                [ariaLabel]="{ by: section.labelId }"
                                [resetSelectedOnCleanup]="true"
                                (valueChange)="onContactSelected($event, section)"
                            />
                        </div>
                    </div>
                </section>
            }

            @if (layout().showApprovalWindow) {
                <section class="notification-policy-sections__block c-margin-bottom--single">
                    <span class="ngBold c-displayBlock">Approval window</span>
                    <label class="c-margin-top--quarter c-margin-right--single"
                        >Days
                        <input
                            class="numeric-wrapper"
                            type="number"
                            min="0"
                            max="65536"
                            [formField]="policyForm().days"
                        />
                    </label>
                    @let daysField = policyForm().days();
                    @if ((daysField.touched() || submitAttempted()) && daysField.invalid()) {
                        <p class="ngRed">{{ daysField.errors()[0].message }}</p>
                    }
                    <label class="c-margin-top--quarter c-margin-right--single"
                        >Hours
                        <input
                            class="numeric-wrapper"
                            type="number"
                            min="0"
                            max="23"
                            [formField]="policyForm().hours"
                        />
                    </label>
                    @let hoursField = policyForm().hours();
                    @if ((hoursField.touched() || submitAttempted()) && hoursField.invalid()) {
                        <p class="ngRed">{{ hoursField.errors()[0].message }}</p>
                    }
                    <label class="c-margin-top--quarter"
                        >Minutes
                        <input
                            class="numeric-wrapper"
                            type="number"
                            min="0"
                            max="59"
                            [formField]="policyForm().minutes"
                        />
                    </label>
                    @let minutesField = policyForm().minutes();
                    @if ((minutesField.touched() || submitAttempted()) && minutesField.invalid()) {
                        <p class="ngRed">{{ minutesField.errors()[0].message }}</p>
                    }
                    <span class="c-displayBlock ngFinePrint">(of event creation date)</span>
                </section>
            }
        }
    `,
    styles: `
        .notification-policy--flex,
        .s25-item-holder {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.25rem;
        }

        .notification-policy--flex .s25-item-name,
        .s25-item-holder .s25-item-name {
            position: relative;
            top: 2px;
        }

        .notification-policy-sections__header {
            display: flex;
            align-items: baseline;
            justify-content: flex-start;
            gap: 0.5rem;
        }

        .notification-policy-sections__body {
            padding-left: 1rem;
            margin-top: 0.25rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25NotificationPolicySectionsComponent {
    readonly policyForm = input.required<FieldTree<NotificationPolicyModel.FormModel>>();
    readonly layout = input<NotificationPolicyModel.LayoutConfig>(NotificationPolicyModel.defaultLayout());
    readonly readOnly = input(false);
    readonly submitAttempted = input(false);
    readonly editor = input.required<NotificationPolicyModel.Editor>();

    protected readonly usersOnlyFilter = USERS_ONLY_FILTER;
    protected readonly editableContactRowContext: EditableContactRowContext;

    protected readonly Item = Item;
    protected readonly quorumOptions = Task.NotificationPolicy.ApproverQuorumOptions;

    readonly draft = computed(() => NotificationPolicyMapper.draftFromForm(this.policyForm()().value()));
    readonly days = computed(() => this.draft().days ?? 0);
    readonly hours = computed(() => this.draft().hours ?? 0);
    readonly minutes = computed(() => this.draft().minutes ?? 0);
    readonly quorumReadOnlyLabel = computed(() => NotificationPolicyMapper.quorumReadOnlyLabel(this.draft()));

    private readonly approverContacts = computed(() =>
        this.draft()
            .assignments.filter((assignment) => assignment.role === "approver")
            .map((assignment) => NotificationPolicyMapper.assignmentToContactDisplay(assignment)),
    );
    private readonly fyiContacts = computed(() =>
        this.draft()
            .assignments.filter((assignment) => assignment.role === "fyi")
            .map((assignment) => NotificationPolicyMapper.assignmentToContactDisplay(assignment)),
    );

    readonly contactSections: readonly ContactSectionConfig[] = [
        {
            role: "approver",
            title: "Approvers",
            removeFallback: "approver",
            moveLabel: "Move to FYI",
            moveTarget: "fyi",
            fieldId: "approverAddContact",
            labelId: "approverAddContact-label",
            contacts: this.approverContacts,
            dropdownSelection: signal<Nullable<DropDownItem>>(null),
        },
        {
            role: "fyi",
            title: "FYI contacts",
            removeFallback: "FYI contact",
            moveLabel: "Move to approvers",
            moveTarget: "approver",
            fieldId: "fyiAddContact",
            labelId: "fyiAddContact-label",
            contacts: this.fyiContacts,
            dropdownSelection: signal<Nullable<DropDownItem>>(null),
        },
    ];

    onQuorumChange(approvalTypeId: number): void {
        const approvalType = this.quorumOptions.find((option) => option.itemId === approvalTypeId);
        if (approvalType) {
            this.editor().setQuorum(approvalType);
        }
    }

    onContactSelected(selected: Nullable<DropDownItem>, section: ContactSectionConfig): void {
        if (!selected?.itemId) {
            return;
        }
        this.runAfterDropdownClose(() => {
            this.editor().addContact(selected, section.role);
            section.dropdownSelection.set(null);
        });
    }

    moveContactToTarget(
        contact: NotificationPolicyModel.ContactDisplay,
        targetRole: NotificationPolicyModel.ContactRole,
    ): void {
        const contactId = S25Util.parseInt(contact.itemId);
        if (contactId != null) {
            this.editor().moveContact(contactId, targetRole);
        }
    }

    removeContact(contact: NotificationPolicyModel.ContactDisplay): void {
        const contactId = S25Util.parseInt(contact.itemId);
        if (contactId != null) {
            this.editor().removeContact(contactId);
        }
    }

    private runAfterDropdownClose(action: () => void) {
        setTimeout(() => action(), 0);
    }
}
